import React, { useLayoutEffect, useState } from 'react';
import ProgressiveImage from 'react-progressive-graceful-image';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { BsImage } from '@react-icons/all-files/bs/BsImage';
import { compareAsc } from 'date-fns';
import { getImageUrl, ResizeImage } from '@bucketplace/design-system';

import { Duration } from '../../components/Duration';
import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { useEditorInfoContext } from '../../contexts';
import { useEditorNode } from '../../hooks';
import { DocumentPath } from '../../types';
import { ImageMapBrazeProps } from './ImageMapBraze/ImageMapBraze';
import { ImageMapLinkProps } from './ImageMapLink/ImageMapLink';
import { ImageMapShareProps } from './ImageMapShare/ImageMapShare';

export interface ImageProps {
  src?: string;
  children: React.ReactElement<ImageMapLinkProps | ImageMapShareProps | ImageMapBrazeProps>;
  duration?: Duration;
  useDuration?: boolean;
  path: DocumentPath;
}

export function Image(props: ImageProps) {
  const { src: originalSrc, children, duration, path } = props;
  const { isInitialLoadAllContent } = useEditorInfoContext();
  const { displayMode, displayDate } = useEditorNode(path);
  const [onDuration, setOnDuration] = useState(false);

  useLayoutEffect(() => {
    if (!duration) { return setOnDuration(true); }
    let interval: number;

    const compareDisplayDateToDuration = () => {
      const currentDate = displayDate ?? new Date();

      if (duration.startAt) {
        const beforeStart = compareAsc(currentDate, new Date(duration.startAt)) < 0;
        if (beforeStart) {
          return setOnDuration(false);
        }
      }
      if (duration.endAt) {
        const beforeEnd = compareAsc(currentDate, new Date(duration.endAt)) < 0;
        if (beforeEnd) {
          return setOnDuration(true);
        }
        setOnDuration(false);
        clearInterval(interval);
        return;
      }

      setOnDuration(true);
      clearInterval(interval);
    };

    compareDisplayDateToDuration();
    interval = window.setInterval(compareDisplayDateToDuration, 1000);
    return () => clearInterval(interval);
  }, [duration?.startAt, duration?.endAt, displayDate]);

  if (!onDuration && displayMode !== 'edit') { return null; }

  if (!originalSrc) {
    return (
      <EditorPlaceholder icon={<BsImage />} message="이미지를 등록해주세요." />
    );
  }

  const src = getImageUrl({ src: originalSrc });

  return (
    <Container>
      { isInitialLoadAllContent
        ? (
          <ProgressiveImage
            src={src}
            placeholder={`${src}?w=50`}
            rootMargin="70%"
            srcSetData={{
              srcSet: `
              ${src}?w=320 320w,
              ${src}?w=768 768w,
              ${src}?w=768 1024w
            `,
              sizes: '(max-width: 1024px) 100vw, 1024px',
            }}
          >
            { (src, loading, srcSetData) => (
              <Img
                data-testid="progressive-image"
                src={src}
                srcSet={srcSetData?.srcSet}
                sizes={srcSetData?.sizes}
                showloading={loading}
              />
            ) }
          </ProgressiveImage>
        )
        : (
          <StyledResizeImage
            src={originalSrc}
          />
        ) }
      { children }
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  font-size: 0;
`;

const Img = styled.img<{ showloading?: boolean }>`
  display: block;
  width: 100%;
  ${({ showloading }) => showloading && css`
    filter: blur(10px);
    clip-path: inset(0);
  `}
`;

const StyledResizeImage = styled(ResizeImage)`
  width: 100%;
`;
